import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { chatsApi, apiClient } from '../utils/apiClient';
import { getSessionAgent } from '../utils/session';
import { getChatSocket } from '../utils/socketClient';

interface ChatMessage {
  id: string | number;
  text: string;
  sender: 'me' | 'user';
  createdAt?: string;
  messageType?: 'text' | 'file';
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentSize?: number;
  mimeType?: string;
}

interface ChatSummary {
  id: string;
  userId: string;
  externalId: string;
  roomId: string;
  status: string;
  assignmentStatus: string;
  assignedAgentId: string;
  sourceApp: string;
  userName?: string;
  lastMessage?: {
    content?: string;
  };
}

function MessagePage() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [connected, setConnected] = useState(false);
  const [chatList, setChatList] = useState<ChatSummary[]>([]);
  const [selectedChat, setSelectedChat] = useState<ChatSummary | null>(null);
  const [chatListLoading, setChatListLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const selectedChatRef = useRef<ChatSummary | null>(null);
  const joinedChatRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const hasAutoScrolledRef = useRef(false);
  // Track recently processed attachment URLs with timestamps to prevent backend duplicates
  const processedAttachmentsRef = useRef<Map<string, number>>(new Map());

  const fetchMessagesForChat = async (chat: ChatSummary) => {
    setMessagesLoading(true);
    setMessages([]);
    setUnreadCount(0);
    hasAutoScrolledRef.current = false;
    const response = await chatsApi.messages(chat.externalId);
    console.log('[chatMessages] response', response);
    setMessagesLoading(false);
    if (!response.success) {
      toast.error(response.error);
      return;
    }

    const data = response.data.data as Array<{
      id: string;
      content?: string | null;
      senderType: 'user' | 'agent';
      messageType?: 'text' | 'file';
      attachmentUrl?: string | null;
      attachmentName?: string | null;
      attachmentSize?: number | null;
      mimeType?: string | null;
      createdAt?: string;
    }> | undefined;

    const rawMapped = (data ?? [])
      .map((item) => {
        const isFile = item.messageType === 'file' || Boolean(item.attachmentUrl);
        const text = isFile ? item.attachmentUrl ?? '' : item.content ?? '';
        if (!text) return null;
        return {
          id: item.id,
          text,
          sender: item.senderType === 'agent' ? 'me' : 'user',
          createdAt: item.createdAt,
          messageType: isFile ? 'file' : 'text',
          attachmentUrl: isFile ? item.attachmentUrl ?? undefined : undefined,
          attachmentName: isFile ? item.attachmentName ?? undefined : undefined,
          attachmentSize: isFile ? item.attachmentSize ?? undefined : undefined,
          mimeType: isFile ? item.mimeType ?? undefined : undefined,
        } as ChatMessage;
      });
    const mapped: ChatMessage[] = rawMapped.filter((item): item is ChatMessage => item !== null);
    setMessages(mapped);

    try {
      localStorage.setItem(`chat-history-${chat.externalId}`, JSON.stringify(response.data));
    } catch (error) {
      console.warn('Unable to cache chat history', error);
    }
  };

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    const fetchChats = async () => {
      setChatListLoading(true);
      const response = await chatsApi.list();
      console.log('[chatsApi] response', response);
      setChatListLoading(false);
      if (!response.success) {
        toast.error(response.error);
        return;
      }

      const data = response.data.data as ChatSummary[] | undefined;
      setChatList(data ?? []);

      try {
        localStorage.setItem('chat-user-list', JSON.stringify(response.data));
      } catch (error) {
        console.warn('Unable to cache chat list', error);
      }

      if (data?.length) {
        setSelectedChat(data[0]);
        await fetchMessagesForChat(data[0]);
      }
    };

    fetchChats();

    const socket = getChatSocket();

    const handleConnect = () => {
      setConnected(true);
      toast.success('Connected to live chat');
      console.log('[socket] connected');
    };

    const handleDisconnect = () => {
      setConnected(false);
      toast('Disconnected from live chat', { icon: '⚠️' });
      console.log('[socket] disconnected');
    };

    const joinSelectedChat = () => {
      const activeChat = selectedChatRef.current;
      const nextChatId = activeChat?.roomId ?? activeChat?.externalId ?? null;
      if (!nextChatId) return;

      if (joinedChatRef.current && joinedChatRef.current !== nextChatId) {
        socket.emit('chat:leave', { chatId: joinedChatRef.current });
      }
      joinedChatRef.current = nextChatId;

      socket.emit(
        'chat:join',
        { chatId: nextChatId },
        (ack: { ok?: boolean; joined?: string } | undefined) => {
          console.log('[socket] chat:join ack', ack);
        },
      );
      console.log('[socket] chat:join', { chatId: nextChatId });
    };

    const handleMessage = (payload: {
      message?: {
        id?: string;
        chatId?: string;
        internalChatId?: string;
        content?: string | null;
        senderType?: 'user' | 'agent';
        messageType?: 'text' | 'file';
        attachmentUrl?: string | null;
        attachmentName?: string | null;
        attachmentSize?: number | null;
        mimeType?: string | null;
        createdAt?: string | null;
      };
      id?: string;
      chatId?: string;
      internalChatId?: string;
      content?: string | null;
      senderType?: 'user' | 'agent';
      messageType?: 'text' | 'file';
      attachmentUrl?: string | null;
      attachmentName?: string | null;
      attachmentSize?: number | null;
      mimeType?: string | null;
      createdAt?: string | null;
    }) => {
      console.log('[socket] chat:message', payload);
      const incoming = payload?.message ?? payload;
      if (!incoming) return;
      const activeChat = selectedChatRef.current;
      const incomingChatId = incoming.chatId ?? incoming.internalChatId;
      if (
        activeChat &&
        incomingChatId &&
        incomingChatId !== activeChat.externalId &&
        incomingChatId !== activeChat.roomId &&
        incomingChatId !== activeChat.id
      ) {
        return;
      }
      const isFile = incoming.messageType === 'file' || Boolean(incoming.attachmentUrl);
      const text = isFile ? incoming.attachmentUrl ?? '' : incoming.content ?? '';
      if (!text) return;
      const sender = incoming.senderType === 'agent' ? 'me' : 'user';
      if (sender === 'user' && !computeIsAtBottom()) {
        setUnreadCount((prev) => prev + 1);
      }
      setMessages((prev) => {
        if (incoming.id && prev.some((message) => message.id === incoming.id)) {
          return prev;
        }
        const last = prev[prev.length - 1];
        if (last && last.text === text && last.sender === sender) {
          return prev;
        }
        // Check for duplicates by attachmentUrl for file messages
        if (isFile && incoming.attachmentUrl) {
          // Normalize incoming attachment URL
          const normalizeKey = (url: string) => {
            if (!url) return '';
            if (url.includes('amazonaws.com')) {
              const match = url.match(/chat\/uploads\/[^?]+/);
              return match ? match[0] : url.replace(/^\//, '');
            }
            return url.replace(/^\//, '');
          };
          const incomingKey = normalizeKey(incoming.attachmentUrl);
          const now = Date.now();
          
          // Check if we already processed this attachment in the last 3 seconds
          const lastProcessed = processedAttachmentsRef.current.get(incomingKey);
          if (lastProcessed && now - lastProcessed < 3000) {
            console.log('[socket] duplicate blocked - already processed:', incomingKey);
            return prev;
          }
          
          // Mark as processed
          processedAttachmentsRef.current.set(incomingKey, now);
          // Clean up old entries after 5 seconds
          setTimeout(() => {
            processedAttachmentsRef.current.delete(incomingKey);
          }, 5000);
          
          // Also check against existing messages in state
          const isDuplicate = prev.some(
            (message) => {
              if (message.messageType !== 'file' || message.sender !== sender) return false;
              const msgKey = normalizeKey(message.attachmentUrl || '');
              const textKey = normalizeKey(message.text || '');
              return msgKey === incomingKey || textKey === incomingKey;
            }
          );
          if (isDuplicate) {
            console.log('[socket] duplicate file message blocked:', incomingKey);
            return prev;
          }
        }
        return [
          ...prev,
          {
            id: incoming.id ?? Date.now(),
            text,
            sender,
            createdAt: incoming.createdAt ?? new Date().toISOString(),
            messageType: isFile ? 'file' : 'text',
            attachmentUrl: isFile ? incoming.attachmentUrl ?? undefined : undefined,
            attachmentName: isFile ? incoming.attachmentName ?? undefined : undefined,
            attachmentSize: isFile ? incoming.attachmentSize ?? undefined : undefined,
            mimeType: isFile ? incoming.mimeType ?? undefined : undefined,
          },
        ];
      });
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('chat:message', handleMessage);

    if (!socket.connected) {
      socket.connect();
    }
    if (socket.connected) {
      joinSelectedChat();
    }

    socket.on('connect', joinSelectedChat);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('chat:message', handleMessage);
      socket.off('connect', joinSelectedChat);
    };
  }, []);

  useEffect(() => {
    const nextChatId = selectedChat?.roomId ?? selectedChat?.externalId ?? null;
    if (!nextChatId) return;

    const socket = getChatSocket();
    if (!socket.connected) {
      joinedChatRef.current = nextChatId;
      socket.connect();
      return;
    }

    if (joinedChatRef.current && joinedChatRef.current !== nextChatId) {
      socket.emit('chat:leave', { chatId: joinedChatRef.current });
    }
    joinedChatRef.current = nextChatId;
    socket.emit('chat:join', { chatId: nextChatId }, (ack: { ok?: boolean; joined?: string } | undefined) => {
      console.log('[socket] chat:join ack', ack);
    });
    console.log('[socket] chat:join', { chatId: nextChatId });
  }, [selectedChat]);

  const handleSendMessage = () => {
    if (!inputValue.trim()) {
      return;
    }

    if (!selectedChat) {
      toast.error('Select a chat before sending a message');
      return;
    }

    const socket = getChatSocket();
    const agent = getSessionAgent();
    if (!agent?.id) {
      toast.error('Agent id missing, cannot send message');
      return;
    }

    const eventPayload = {
      chatId: selectedChat.roomId ?? selectedChat.externalId,
      senderType: 'agent',
      senderId: agent.id,
      messageType: 'text',
      content: inputValue,
    };
    socket.emit('chat:send', eventPayload, (ack: { ok?: boolean; message?: unknown; error?: string } | undefined) => {
      console.log('[socket] chat:send ack', ack);
    });
    console.log('[socket] emitted chat:send', eventPayload);
    setMessages((prev) => [
      ...prev,
      {
        id: `local-${Date.now()}`,
        text: inputValue,
        sender: 'me',
        createdAt: new Date().toISOString(),
      },
    ]);
    setInputValue('');
  };

  const handleImageUpload = async (file: File) => {
    if (!file) return;
    if (!selectedChat) {
      toast.error('Select a chat before sending an image');
      return;
    }

    const maxSizeBytes = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSizeBytes) {
      toast.error('File is larger than 5MB');
      return;
    }

    const isImage = file.type?.startsWith('image/');
    if (!isImage) {
      toast.error('Only image files are allowed');
      return;
    }

    const agent = getSessionAgent();
    if (!agent?.id) {
      toast.error('Agent id missing, cannot send image');
      return;
    }

    setIsUploading(true);
    const clientMessageId = `img-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    try {
      // Step 1: Get presigned URL
      const presignEndpoint = `${process.env.REACT_APP_API_BASE_URL ?? 'https://server.srt.carewinners.com/api/v1'}/chats/uploads/presign`;
      const presignResponse = await apiClient.post(presignEndpoint, {
        fileName: file.name,
        contentType: file.type,
        contentLength: file.size,
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth') ?? ''}`,
        },
      });

      const uploadUrl = (presignResponse as any)?.uploadUrl;
      const key = (presignResponse as any)?.key;
      const maxSize = (presignResponse as any)?.maxSize;

      if (!uploadUrl || !key) {
        throw new Error('Invalid presign response');
      }

      if (typeof maxSize === 'number' && file.size > maxSize) {
        throw new Error('File too large');
      }

      // Step 2: Upload to S3
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
          'Content-Length': file.size,
        },
      });

      // Step 3: Send via HTTP POST (Admin uses HTTP for persistence)
      const chatId = selectedChat.externalId;
      const sendResponse = await chatsApi.sendMessage(chatId, {
        senderType: 'agent',
        senderId: agent.id,
        messageType: 'file',
        attachmentUrl: key,
        attachmentName: file.name,
        attachmentSize: file.size,
        mimeType: file.type,
      });

      if (!sendResponse.success) {
        throw new Error(sendResponse.error || 'Failed to send message');
      }

      console.log('[http] message sent (image)', sendResponse.data);

      // Step 4: Also emit via socket for real-time broadcast to portal
      const socket = getChatSocket();
      // Live Chat uses roomId for socket operations, not externalId
      const emitChatId = selectedChat.roomId || selectedChat.externalId;
      const socketPayload = {
        chatId: emitChatId,
        senderType: 'agent' as const,
        senderId: agent.id,
        messageType: 'file' as const,
        attachmentUrl: key,
        attachmentName: file.name,
        attachmentSize: file.size,
        mimeType: file.type,
      };
      
      console.log('[socket] attempting to emit chat:send', { 
        connected: socket.connected, 
        chatId: emitChatId,
        hasAttachmentUrl: Boolean(key),
        payload: socketPayload,
      });
      
      if (!socket.connected) {
        console.log('[socket] not connected, connecting...');
        socket.connect();
        // Wait a bit for connection and room join
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Ensure we're joined to the chat room before emitting
      const joinAck = await new Promise<{ ok?: boolean }>((resolve) => {
        socket.emit('chat:join', { chatId: emitChatId }, (ack: { ok?: boolean } | undefined) => {
          resolve(ack || { ok: true });
        });
      });
      console.log('[socket] chat:join ack', joinAck);
      
      socket.emit('chat:send', socketPayload, (ack: { ok?: boolean; message?: unknown; error?: string } | undefined) => {
        console.log('[socket] chat:send ack (image)', ack);
      });
      console.log('[socket] emitted chat:send with attachmentUrl:', key);

      // Get server response data
      const serverMessage = (sendResponse.data as any)?.data || sendResponse.data as any;

      // Track this attachment when adding via HTTP response to prevent socket duplicates
      const normalizeKey = (url: string) => {
        if (!url) return '';
        if (url.includes('amazonaws.com')) {
          const match = url.match(/chat\/uploads\/[^?]+/);
          return match ? match[0] : url.replace(/^\//, '');
        }
        return url.replace(/^\//, '');
      };
      const normalizedKey = normalizeKey(serverMessage?.attachmentUrl || key);
      processedAttachmentsRef.current.set(normalizedKey, Date.now());
      setTimeout(() => {
        processedAttachmentsRef.current.delete(normalizedKey);
      }, 5000);

      // Optimistic UI update with server response if available
      setMessages((prev) => [
        ...prev,
        {
          id: serverMessage?.id || clientMessageId,
          text: serverMessage?.attachmentUrl || key,
          sender: 'me',
          createdAt: serverMessage?.createdAt || new Date().toISOString(),
          messageType: 'file',
          attachmentUrl: serverMessage?.attachmentUrl || key,
          attachmentName: serverMessage?.attachmentName || file.name,
          attachmentSize: serverMessage?.attachmentSize || file.size,
          mimeType: serverMessage?.mimeType || file.type,
        },
      ]);

      toast.success('Image sent');
    } catch (error) {
      console.error('Image upload failed', error);
      toast.error(error instanceof Error ? error.message : 'Image upload failed');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleImageUpload(file);
  };

  const formatTime = (iso?: string) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const computeIsAtBottom = () => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    const threshold = 24;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  };

  const scrollToBottom = (behavior: ScrollBehavior) => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  };

  useEffect(() => {
    if (!messages.length) return;
    const last = messages[messages.length - 1];
    const isInitial = !hasAutoScrolledRef.current && !messagesLoading;
    const isOutgoing = last.sender === 'me';
    if (!isInitial && !isOutgoing) return;
    hasAutoScrolledRef.current = true;
    scrollToBottom(isInitial ? 'auto' : 'smooth');
  }, [messages, messagesLoading]);

  const handleMessagesScroll = () => {
    if (computeIsAtBottom() && unreadCount > 0) {
      setUnreadCount(0);
    }
  };

  const statusLabel = useMemo(() => (connected ? 'Online' : 'Offline'), [connected]);

  const renderMessageContent = (message: ChatMessage) => {
    const isFile = message.messageType === 'file' || Boolean(message.attachmentUrl);
    if (!isFile) {
      return <span className="whitespace-pre-wrap break-words">{message.text}</span>;
    }

    const url = message.attachmentUrl ?? message.text;
    const trimmed = (url ?? '').trim();
    if (!trimmed) return null;

    const isImageMime = (message.mimeType ?? '').toLowerCase().startsWith('image/');
    const isImageUrl = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((trimmed.split('?')[0] ?? '').trim());
    const shouldRenderImage = isImageMime || isImageUrl;

    if (shouldRenderImage) {
      return (
        <img
          src={trimmed}
          alt={message.attachmentName ?? 'uploaded'}
          className="max-h-56 max-w-full rounded-xl border border-slate-200 object-contain bg-white"
          loading="lazy"
        />
      );
    }

    return (
      <a
        href={trimmed}
        target="_blank"
        rel="noopener noreferrer"
        className="text-slate-900 underline break-words"
      >
        {message.attachmentName ?? 'Download file'}
      </a>
    );
  };

  return (
    <section className="flex gap-6 h-full min-h-0 overflow-hidden">
      <aside className="w-64 h-full rounded-3xl border border-slate-200/70 bg-white shadow-lg shadow-slate-200/70 overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="text-xs uppercase tracking-[0.4em] text-emerald-500">Messages</p>
          <p className="text-lg font-semibold text-slate-900">Active chats</p>
        </div>
        <div className="space-y-1 px-4 py-3">
          {chatListLoading && (
            <div className="space-y-3">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="flex items-center gap-3 rounded-2xl px-3 py-2">
                  <div className="h-10 w-10 rounded-full bg-slate-100 animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-24 rounded bg-slate-100 animate-pulse" />
                    <div className="h-2 w-32 rounded bg-slate-100 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!chatListLoading && chatList.length === 0 && (
            <div className="text-sm text-slate-500 px-2 py-4">No chats available yet.</div>
          )}

          {!chatListLoading &&
            chatList.map((item) => {
              const status = 'online';
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setSelectedChat(item);
                    fetchMessagesForChat(item);
                  }}
                  className={`flex w-full items-center gap-3 rounded-2xl px-3 py-2 transition hover:bg-emerald-50 ${selectedChat?.id === item.id ? 'bg-emerald-50' : ''}`}
                >
                  <div
                    className={`h-10 w-10 rounded-full border ${status === 'online' ? 'border-emerald-400 bg-emerald-100' : 'border-slate-200 bg-slate-100'}`}
                  />
                  <div className="flex-1 text-sm">
                    <p className="font-semibold text-slate-900">{item.userName ?? `User ${item.userId}`}</p>
                    <p className="text-xs text-slate-500">{item.lastMessage?.content ?? 'No messages yet'}</p>
                  </div>
                  <span className={`h-2 w-2 rounded-full ${status === 'online' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                </button>
              );
            })}
        </div>
        <div className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
          Showing {chatList.length} chats
        </div>
      </aside>
      <section className="flex-1 flex flex-col min-h-0 rounded-3xl border border-slate-200/70 bg-white shadow-lg shadow-slate-200">
        <header className="flex-shrink-0 flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-slate-100" />
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {selectedChat?.userName ?? (selectedChat ? `User ${selectedChat.userId}` : 'Select a chat')}
              </p>
              <p className="text-xs text-slate-500">{selectedChat ? statusLabel : 'No chat selected'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm font-semibold text-emerald-600">
            <button className="rounded-full bg-emerald-50 px-3 py-1">Call</button>
            <span className="text-2xl">…</span>
          </div>
        </header>
        <div
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          className="flex-1 min-h-0 overflow-y-auto p-6"
        >
          {messagesLoading && (
            <div className="space-y-4">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-slate-100 animate-pulse" />
                  <div className="h-10 w-56 rounded-2xl bg-slate-100 animate-pulse" />
                </div>
              ))}
            </div>
          )}

          {!messagesLoading && messages.length === 0 && (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              Select a chat to view messages.
            </div>
          )}

          {!messagesLoading && messages.length > 0 && (
            <div className="flex flex-col gap-6 justify-end min-h-full">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start gap-3 ${message.sender === 'me' ? 'justify-end' : 'justify-start'
                    }`}
                >
                  {message.sender === 'user' && (
                    <div className="h-10 w-10 rounded-full bg-gradient-to-b from-emerald-200 to-emerald-400" />
                  )}
                  <div className="flex flex-col max-w-[70%]">
                    <div
                      className={`rounded-2xl px-4 py-3 text-sm leading-relaxed shadow ${message.sender === 'me'
                        ? 'bg-emerald-100 text-slate-800'
                        : 'bg-slate-100 text-slate-800'
                        }`}
                    >
                      {renderMessageContent(message)}
                    </div>
                    {message.createdAt && (
                      <span
                        className={`mt-1 text-[11px] text-slate-400 ${
                          message.sender === 'me' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {formatTime(message.createdAt)}
                      </span>
                    )}
                  </div>
                  {message.sender === 'me' && (
                    <div className="h-10 w-10 rounded-full bg-gradient-to-b from-emerald-200 to-emerald-400" />
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}

          {unreadCount > 0 && (
            <div className="sticky bottom-4 flex justify-center pointer-events-none z-10">
              <button
                type="button"
                onClick={() => {
                  setUnreadCount(0);
                  scrollToBottom('smooth');
                }}
                className="pointer-events-auto rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold text-white shadow-lg"
              >
                {unreadCount} new message{unreadCount === 1 ? '' : 's'}
              </button>
            </div>
          )}
        </div>
        <footer className="flex-shrink-0 border-t border-slate-100 bg-slate-50 px-6 py-4">
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-emerald-600 transition disabled:opacity-50"
              title="Send image"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            </button>
            <input
              type="text"
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSendMessage();
                }
              }}
              placeholder={isUploading ? 'Uploading image...' : 'Type message...'}
              disabled={isUploading}
              className="flex-1 border-none bg-transparent text-sm leading-relaxed outline-none disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleSendMessage}
              disabled={isUploading || !inputValue.trim()}
              className="rounded-full bg-[#78C45E] p-2 text-white shadow hover:bg-[#78C45E]/90 disabled:opacity-50"
            >
              <span className="text-xs uppercase tracking-wider">{isUploading ? '...' : 'Send'}</span>
            </button>
          </div>
        </footer>
      </section>
    </section>
  );
}

export default MessagePage;
